import { Banner, BlockStack, Button, ChoiceList, InlineStack, Modal, Text, TextField } from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useAuthStore, useLookupStore } from '../../../context/useStore';
import { getUserOrg } from '../functions';
import { formatToCurrency } from '../functions';

/*
  Pick a colleague: assign (accounting), reassign the whole document, or hand off specific lines.
  suggestions: card.suggestions?.assignees ([{ userId, Afkorting, name, score, reasons }])
*/
const TITLES = { assign: 'Assign for approval', reassign: 'Reassign to a colleague', handoff: 'Hand off to a colleague' };

export default function AssigneeModal({ open, onClose, mode = 'reassign', suggestions = [], defaultUserId = null, lines = [], defaultLineIds = null, onConfirm, busy = false, warning = null }) {
  const users = useLookupStore((state) => state.users);
  const organisations = useLookupStore((state) => state.organisations);
  const { user } = useAuthStore();
  const userOrg = user && getUserOrg(organisations, user.ORGANISATION_NAME);
  const [userId, setUserId] = useState(defaultUserId);
  const [note, setNote] = useState('');
  const [lineIds, setLineIds] = useState(defaultLineIds || []);

  useEffect(() => {
    if (open) {
      setUserId(defaultUserId || null);
      setNote('');
      setLineIds(defaultLineIds || []);
    }
  }, [open, defaultUserId, defaultLineIds]);

  const options = useMemo(
    () =>
      users
        .filter((u) => !u.Inactief && u.UserType !== 7 && String(u.Afkorting || '').toUpperCase() !== 'SYS' && (!userOrg || u.Vennootschap === userOrg) && u.Email)
        .sort((a, b) => String(a.Afkorting).localeCompare(String(b.Afkorting)))
        .map((u) => ({ value: u.GebruikerID, label: `${u.Afkorting} · ${u.Gebruikernaam}` })),
    [users, userOrg]
  );
  const selected = options.find((o) => o.value === userId) || null;
  const pendingLines = lines.filter((line) => line.editable !== false);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={TITLES[mode] || TITLES.reassign}
      primaryAction={{
        content: mode === 'assign' ? 'Assign & send' : 'Send to colleague',
        onAction: () => onConfirm({ toUserId: userId, note: note.trim() || null, lineIds: mode === 'handoff' ? lineIds : null }),
        disabled: !userId || (mode === 'handoff' && lineIds.length === 0),
        loading: busy
      }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {suggestions.length ? (
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued">
                Suggested
              </Text>
              <InlineStack gap="200" wrap>
                {suggestions.slice(0, 4).map((s) => (
                  <Button key={s.userId} size="slim" pressed={userId === s.userId} onClick={() => setUserId(s.userId)}>
                    {`${s.Afkorting || s.userId} · ${Math.round((s.score || 0) * 100)}%`}
                  </Button>
                ))}
              </InlineStack>
              {selected && suggestions.find((s) => s.userId === userId)?.reasons?.length ? (
                <Text variant="bodySm" tone="subdued">
                  {suggestions.find((s) => s.userId === userId).reasons.join(' · ')}
                </Text>
              ) : null}
            </BlockStack>
          ) : null}
          <div>
            <Text variant="bodySm">Colleague</Text>
            <Select
              options={options}
              value={selected}
              onChange={(option) => setUserId(option ? option.value : null)}
              placeholder="Search a colleague…"
              isClearable
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
          {mode === 'handoff' && pendingLines.length > 1 ? (
            <ChoiceList
              allowMultiple
              title="Lines to hand off"
              choices={pendingLines.map((line) => ({
                value: String(line.id ?? line._key),
                label: `${formatToCurrency(line.Bedrag)} ${line.projectNumber ? `· ${line.projectNumber}` : ''} ${line.Notitie ? `· ${line.Notitie}` : ''}`
              }))}
              selected={lineIds.map(String)}
              onChange={(values) => setLineIds(values)}
            />
          ) : null}
          <TextField label="Note for your colleague (optional)" value={note} onChange={setNote} multiline={2} autoComplete="off" maxLength={500} showCharacterCount />
          {warning ? <Banner tone="warning">{warning}</Banner> : null}
          <Banner tone="info">An email with a link to the document is sent right away.</Banner>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
